if (!window.Splide) await import('./splide.min.js');
const Splide = window.Splide;

if (!customElements.get('flex-pro-steps')) {
  customElements.define(
    'flex-pro-steps',
    class MediaSlider extends HTMLElement {
      disconnectedCallback() {
        this.splideInstance?.destroy();
        this.splideInstance = null;
      }

      connectedCallback() {
        this.splideInstance?.destroy();
        const track = this.querySelector('.splide');
        if (!track) return;

        const init = () => {
          if (typeof Splide === 'undefined') return;

          const defaults = {
            perPage: parseInt(this.dataset.perPage) || 3,
            perMove: 1,
            gap: 16,
            arrows: true,
            pagination: true,
            speed: 400,
            breakpoints: {
              750: {
                perPage: 1,
                gap: 8,
              },
            },
          };

          let custom = {};
          const raw = this.getAttribute('m-json');
          if (raw) {
            try {
              custom = JSON.parse(raw);
            } catch (e) {
              console.warn('[flex-pro-steps] m-json parse error', e);
            }
          }

          this.splideInstance = new Splide(track, Object.assign({}, defaults, custom)).mount();

          const prevBtn = this.querySelector(
            '.ikier-flex-pro-steps__btn--prev, [data-slider-prev]',
          );
          const nextBtn = this.querySelector(
            '.ikier-flex-pro-steps__btn--next, [data-slider-next]',
          );

          if (prevBtn) prevBtn.addEventListener('click', () => this.splideInstance.go('<'));
          if (nextBtn) nextBtn.addEventListener('click', () => this.splideInstance.go('>'));

          if (prevBtn || nextBtn) {
            const syncEnds = () => {
              const end = this.splideInstance.Components.Controller.getEnd();
              if (prevBtn) prevBtn.disabled = this.splideInstance.index <= 0;
              if (nextBtn) nextBtn.disabled = this.splideInstance.index >= end;
            };
            this.splideInstance.on('mounted move updated', syncEnds);
            syncEnds();
          }
        };

        init();
      }
    },
  );
}

if (!customElements.get('flex-pro-materials')) {
  customElements.define(
    'flex-pro-materials',
    class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        this.groups = new Map();
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.activate(index), options);
          tab.addEventListener(
            'keydown',
            (event) => {
              let next;
              if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
              if (event.key === 'ArrowLeft')
                next = (index + this.tabs.length - 1) % this.tabs.length;
              if (event.key === 'Home') next = 0;
              if (event.key === 'End') next = this.tabs.length - 1;
              if (next === undefined) return;
              event.preventDefault();
              this.tabs[next].focus();
              this.activate(next);
            },
            options,
          );
        });

        const initialize = () => {
          if (!this.isConnected || !this.nearViewport || typeof Splide === 'undefined') return;
          this.activate(
            Math.max(
              0,
              this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'),
            ),
          );
        };
        this.nearViewport = false;
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.nearViewport = true;
            this.observer.disconnect();
            initialize();
          },
          { rootMargin: '800px 0px' },
        );
        this.observer.observe(this);
        if (typeof Splide === 'undefined') {
          window.addEventListener('load', initialize, { ...options, once: true });
        }
      }

      activate(index) {
        this.tabs.forEach((tab, tabIndex) => {
          const active = tabIndex === index;
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
          this.panels[tabIndex].hidden = !active;
        });
        if (typeof Splide === 'undefined') return;
        const panel = this.panels[index];
        const existing = this.groups.get(panel);
        if (existing) {
          existing.gallery.refresh();
          existing.navigation.refresh();
          this.loadActiveImage(existing.gallery, panel);
          return;
        }
        const gallery = new Splide(panel.querySelector('[data-material-gallery]'), {
          type: 'fade',
          rewind: true,
          perPage: 1,
          arrows: false,
          pagination: false,
          speed: 350,
        });
        const navigation = new Splide(panel.querySelector('[data-material-nav]'), {
          perPage: 8,
          perMove: 1,
          gap: 24,
          isNavigation: true,
          arrows: false,
          pagination: false,
          focus: 0,
          breakpoints: { 750: { perPage: 3, gap: 8, arrows: true } },
        });
        gallery.sync(navigation);
        gallery.on('mounted', () => this.loadActiveImage(gallery, panel));
        gallery.on('move', (index) => this.loadActiveImage(gallery, panel, index));
        gallery.mount();
        navigation.mount();
        this.groups.set(panel, { gallery, navigation });
      }

      loadImage(image, priority = 'auto') {
        if (!image) return;
        image.fetchPriority = priority;
        image.loading = 'eager';
        image
          .closest('picture')
          .querySelectorAll('source[data-srcset]')
          .forEach((source) => {
            source.srcset = source.dataset.srcset;
            source.removeAttribute('data-srcset');
          });
        if (image.dataset.srcset) {
          image.srcset = image.dataset.srcset;
          image.removeAttribute('data-srcset');
        }
        if (image.dataset.src) {
          image.src = image.dataset.src;
          image.removeAttribute('data-src');
        }
      }

      loadActiveImage(gallery, panel, index = gallery.index) {
        const images = panel.querySelectorAll('[data-material-gallery] img');
        const image = images[index];
        if (!image) return;
        this.loadImage(image);
        if (!navigator.connection?.saveData) {
          this.loadImage(images[(index + 1) % images.length], 'low');
        }
        const refresh = () => {
          if (this.isConnected && !panel.hidden) gallery.refresh();
        };
        if (image.complete && image.naturalWidth) {
          requestAnimationFrame(refresh);
        } else {
          image.addEventListener('load', refresh, {
            once: true,
            signal: this.controller.signal,
          });
        }
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.observer?.disconnect();
        this.groups?.forEach(({ gallery, navigation }) => {
          gallery.destroy();
          navigation.destroy();
        });
        this.groups?.clear();
      }
    },
  );
}

if (!customElements.get('flex-pro-scenarios')) {
  customElements.define(
    'flex-pro-scenarios',
    class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        const items = Array.from(this.querySelectorAll('.ikier-flex-pro__scenario'));
        const images = Array.from(this.querySelectorAll('[data-scenario-image]'));
        const buttons = items.map((item) => item.querySelector('button'));
        const desktop = window.matchMedia('(min-width: 751px)');
        const loadImage = (index) => {
          const image = desktop.matches ? images[index] : items[index].querySelector('img');
          image.loading = 'eager';
        };
        const loadActiveImage = () =>
          loadImage(
            Math.max(
              0,
              items.findIndex((item) => item.classList.contains('is-active')),
            ),
          );
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            loadActiveImage();
            this.observer.disconnect();
          },
          { rootMargin: '600px 0px' },
        );
        this.observer.observe(this);
        desktop.addEventListener('change', loadActiveImage, options);
        const activate = (index) => {
          items.forEach((item, itemIndex) => {
            const active = itemIndex === index;
            item.classList.toggle('is-active', active);
            buttons[itemIndex].setAttribute('aria-expanded', String(active));
            item.querySelector('.ikier-flex-pro__scenario-panel').hidden = !active;
            images[itemIndex].hidden = !active;
            if (active) {
              loadImage(itemIndex);
            }
          });
        };
        buttons.forEach((button, index) => {
          button.addEventListener('click', () => activate(index), options);
          button.addEventListener('pointerenter', () => loadImage(index), options);
          button.addEventListener('focus', () => loadImage(index), options);
          button.addEventListener(
            'keydown',
            (event) => {
              let target;
              if (event.key === 'ArrowDown') target = (index + 1) % buttons.length;
              if (event.key === 'ArrowUp') target = (index + buttons.length - 1) % buttons.length;
              if (event.key === 'Home') target = 0;
              if (event.key === 'End') target = buttons.length - 1;
              if (target === undefined) return;
              event.preventDefault();
              buttons[target].focus();
            },
            options,
          );
        });
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.observer?.disconnect();
      }
    },
  );
}
